import "./productSummary.scss";

import React, { useContext, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";

import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import ProductSummaryAttributes from "./ProductSummaryAttributes";
import ProductSummaryClimaticZone from "./ProductSummaryClimaticZone";
import ProductSummaryHeader from "./ProductSummaryHeader";
import ProductSummaryInfo from "./ProductSummaryInfo";
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";
import { IProduct } from "@sbi-international/boutik.gatsby.plugin.services";
import { pushProductViewEvent } from "../../../utils/useGTM_UA";
import { ProductReviewsModel } from "../../../models/productReviewsModel";

type ProductSummaryProps = {
    product: Queries.ProductsJson;
    productReviews: ProductReviewsModel | null
    setDetailsBox: (arg: string) => void;
    reviewDivRef: any;
};

export default function ProductSummary(props: ProductSummaryProps) {
    const { product, setDetailsBox, reviewDivRef, productReviews } = props;

    const [productData, setProductData] = useState<IProduct>();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const { currentLocale } = usePageLocalization();

    const productTitle = product.title ? (product.title[currentLocale] as string) : "";
    const productCode = product.code ? (product.code as string) : "";
    const productCategory = product.category ? (product.category as string) : "";

    useEffect(() => {
        if (product.code) {
            productService.operations.getProduct(product.code).then((product) => {
                product && setProductData(product);
            });
        }
    }, []);

    const scrollToReviews = () => {
        window.scrollTo({
            left: 0,
            top: reviewDivRef.current.offsetTop,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        pushProductViewEvent({
            name: productTitle,
            id: productCode,
            price: productData?.currentPrice?.finalPrice?.value!,
            currency: productData?.currentPrice?.finalPrice?.currency,
            category: productCategory,
        });
    }, [
        currentLocale,
        productData?.currentPrice?.finalPrice.currency,
        productData?.currentPrice?.finalPrice.value,
        product.category,
        product.code,
        product.title,
    ]);

    const gocCreditData = useStaticQuery(
        graphql`
            query {
                govCreditUsa: allProductsJson(filter: { code: { eq: "century_tax-credit-usa" } }) {
                    nodes {
                        category
                        code
                        title {
                            en_US
                        }
                        documents {
                            en_US
                        }
                    }
                }
            }
        `
    );
    const govCreditUsa = gocCreditData?.govCreditUsa?.nodes[0];

    const [currentPrice, setCurrentPrice] = useState<number>();
    useEffect(() => {
        setCurrentPrice(productData?.currentPrice?.finalPrice?.value ?? undefined);
    }, [productData]);

    const getAmount = (price: any): number => {
        return price?.finalPrice?.value ? Math.round(price.finalPrice.value * 100) : NaN;
    };

    const isOnSale = (price: any): boolean => {
        return !!price?.discount?.amountOff;
    };

    return (
        <div className="productSummary">
            <ProductSummaryHeader
                setDetailsBox={setDetailsBox}
                scrollToReviews={scrollToReviews}
                productReviews={productReviews}
                product={product}
            />

            <ProductSummaryInfo product={product} currentPrice={currentPrice} />

            <div className="details">
                <ProductSummaryAttributes product={product} productData={productData} />
                <ProductSummaryClimaticZone isOnSale={!!productData?.currentPrice?.discount?.amountOff} />
            </div>
        </div>
    );
}
